import { useEffect, useRef } from "react";
import styled from "styled-components";
import QRCodeStyling from "qr-code-styling";
import { DownloadQrIcon, LocationIcon } from "./Icons";
import { getHeaderTheme } from "../config/headerTheme";

const QR_THEME = getHeaderTheme();
const QR_COLOR = QR_THEME.accent;
const QR_BG = "#ffffff";

const QrStyle = (text, displaySize = 200) => ({
  width: displaySize,
  height: displaySize,
  type: "svg",
  data: text,
  dotsOptions: { color: QR_COLOR, type: "rounded" },
  backgroundOptions: { color: QR_BG },
  cornersSquareOptions: { type: "extra-rounded", color: QR_COLOR },
  cornersDotOptions: { type: "dot", color: QR_COLOR },
});

const QRCodeDisplay = ({
  text,
  userName = "",
  onUpdate = () => { },
  type,
  sizeCm = 3,
  displaySize = 200,
  showUpdateButton = true,
}) => {
  const qrRef = useRef(null);
  const qrCode = new QRCodeStyling(QrStyle(text, displaySize));

  useEffect(() => {
    if (qrRef.current) {
      qrRef.current.innerHTML = "";
      qrCode.update(QrStyle(text, displaySize));
      qrCode.append(qrRef.current);
    }
  }, [text, displaySize]);

  const handleDownload = async () => {
    const DPI = 600;
    const cmToPx = (cm) => cm * DPI;
    const qrSizePx = cmToPx(sizeCm);
    const extraHeight = cmToPx(0.5);
    const fontSize = 720;

    await qrCode.update(QrStyle(text, displaySize));
    const blob = await qrCode.getRawData("png");
    const image = new Image();
    const url = URL.createObjectURL(blob);

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = qrSizePx;
      canvas.height = qrSizePx + extraHeight;

      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(image, 0, 0, qrSizePx, qrSizePx);

      ctx.fillStyle = QR_COLOR;
      ctx.font = `bold ${Math.round(fontSize * 0.4)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(userName, qrSizePx / 2, qrSizePx + extraHeight * 0.7);

      const link = document.createElement("a");
      link.download = `qr_${userName.replace(/\s+/g, "_").toLowerCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();

      URL.revokeObjectURL(url);
    };
    image.src = url;
  };

  return (
    <Container>

      <QrCode ref={qrRef} />

      <ButtonsContainer>
        {showUpdateButton && (
          <IconButton onClick={onUpdate}>
            <LocationIcon />
          </IconButton>
        )}

        {text && (
          <IconButton onClick={handleDownload}>
            <DownloadQrIcon />
          </IconButton>
        )}

      </ButtonsContainer>
    </Container>
  );
};

export default QRCodeDisplay;

/* === Styled Components === */

const Container = styled.div`
  height: auto;
`;

const QrCode = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ButtonsContainer = styled.div`
  display: grid;
  align-items: center;
  justify-content: center;
  height: 60px;
  margin-bottom: 60px;
  grid-template-columns: repeat(auto-fit, minmax(2px, auto));
  gap: 1rem;

  svg path {
    stroke: ${() => getHeaderTheme().accent};
  }
`;

const IconButton = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 100px;
  background: #f1f1f1;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${() => getHeaderTheme().primaryLight};
    transform: scale(1.05);
  }
`;
